<template>
  <div
    ref="elementRef"
    v-style="
      brandStyles[
        isFullHeight && !hasComponentPercentHeight
          ? 'containerFullHeight' : 'container'
      ]?.(headerHeight, promoBarHeight)
    "
    class="relative"
    :class="[
      roundedCornersClasses,
      usesPercentHeight && 'flex min-h-0 flex-col overflow-hidden',
      {
        '<md:children:h-inherit md:children:h-inherit': usesPercentHeightSmMd && overlaySmall?.includes('overlay'),
        'md:children:h-inherit <md:flex <md:flex-col <md:overflow-hidden':
          usesPercentHeightSmMd && !overlaySmall?.includes('overlay'),
        'lg:children:h-inherit o:lg:h-$h-lg': usesPercentHeightLg,
        'children:h-inherit': !hasComponentPercentHeight,
        'o:h-$h': usesPercentHeightSmMd && usesPercentHeightLg,
        'o:<lg:h-$h': usesPercentHeightSmMd && !usesPercentHeightLg,
      },
    ]"
    :style="percentHeightStyle"
  >
    <define-heading-template>
      <div>
        <cms-base-eyebrow
          class="mb-2"
          :eyebrow-logo-responsive
          :eyebrow-logo-width-responsive
          :eyebrow-text
          :eyebrow-text-color
          :eyebrow-text-html
          :eyebrow-text-style
          :position="{ sm: textAlignmentSmall, md: textAlignmentLarge }"
        />
        <h1 v-if="title" v-style:c="titleColor" :class="titleStyle">
          <base-link
            v-if="mediaLink"
            :data-cta="mediaLink.delocalizedTracking"
            :target="mediaLink.targetAttribute"
            :to="mediaLink.url"
            @click="handlePromotionClick('title')"
          >
            <cms-plain-text :content field-name="title" />
          </base-link>
          <template v-else>
            <cms-plain-text :content field-name="title" />
          </template>
        </h1>
      </div>
    </define-heading-template>
    <div
      class="relative grid overflow-hidden"
      :class="{
        'children:h-inherit': isFullHeight,
        '<md:children:h-inherit': !isFullHeight && usesPercentHeightSmMd && overlaySmall?.includes('overlay'),
        'md:children:h-inherit': !isFullHeight && usesPercentHeightSmMd,
        'lg:children:h-inherit': !isFullHeight && usesPercentHeightLg,
        '<md:flex <md:flex-col <md:flex-1 <md:min-h-0': usesPercentHeightSmMd && !overlaySmall?.includes('overlay'),
        'min-h-0 flex-1': usesPercentHeight,
      }"
    >
      <div
        v-if="parallaxOffset"
        v-style="{ pb: parallaxPaddingBottom }"
        class="relative md:grid-area-stack"
        :class="[
          {
            '<md:grid-area-stack': !usesPercentHeightSmMd || overlaySmall?.includes('overlay'),
            '<md:h-full': usesPercentHeightSmMd && overlaySmall?.includes('overlay'),
            '<md:flex-1 <md:min-h-0 <md:overflow-hidden': usesPercentHeightSmMd && !overlaySmall?.includes('overlay'),
            'md:h-full md:overflow-hidden': usesPercentHeightSmMd,
            'lg:h-full lg:overflow-hidden': usesPercentHeightLg,
          },
          { 'h-full': !usesPercentHeight },
        ]"
      >
        <div ref="gradientAreaRef" class="absolute-0 flex items-center overflow-hidden">
          <div ref="parallaxRef" class="w-full" :style="{ height: `calc(100% + ${parallaxTranslate})` }">
            <base-picture
              v-if="responsiveMedia.images"
              v-bind="responsiveMedia.images"
              class="full"
              :fetchpriority
              fit="cover"
              :lazy
              :position="objectPosition"
              :props-img="imageProps"
            />
            <vf-video
              v-if="responsiveMedia.video"
              v-bind="responsiveMedia.video"
              ref="videoRef"
              :autoplay="!lazy"
              class="full"
              :controls
              :directives="{ 'v-style': { objectPosition } }"
              fit="cover"
              loop
              :meta="responsiveMedia.video.sm || responsiveMedia.video.meta"
            />
          </div>
          <cms-base-gradient
            v-if="gradientStyle && gradientStyle !== 'off'"
            v-bind="{ gradientTone, gradientStyle, gradientGrid, gradientBreakpoints }"
            :alignment="gradientAlignment"
            :position="gradientPosition"
            :text-area-only-extent
          />
        </div>
      </div>

      <div
        v-else
        ref="gradientAreaRef"
        class="relative flex md:grid-area-stack"
        :class="{
          '<md:grid-area-stack': !usesPercentHeightSmMd || overlaySmall?.includes('overlay'),
          '<md:h-full': usesPercentHeightSmMd && overlaySmall?.includes('overlay'),
          '<md:flex-1 <md:min-h-0 <md:overflow-hidden': usesPercentHeightSmMd && !overlaySmall?.includes('overlay'),
          'md:h-full md:overflow-hidden': usesPercentHeightSmMd,
          'lg:h-full lg:overflow-hidden': usesPercentHeightLg,
        }"
      >
        <base-picture
          v-if="responsiveMedia.images"
          v-bind="responsiveMedia.images"
          class="full"
          :fetchpriority
          fit="cover"
          :lazy
          :position="objectPosition"
          :props-img="imageProps"
        />
        <vf-video
          v-if="responsiveMedia.video"
          v-bind="responsiveMedia.video"
          ref="videoRef"
          :autoplay="!lazy"
          class="full"
          :controls
          :directives="{ 'v-style': { objectPosition } }"
          fit="cover"
          loop
          :meta="responsiveMedia.video.sm || responsiveMedia.video.meta"
        />
        <base-link
          v-if="mediaLink"
          aria-hidden="true"
          class="absolute-0"
          :data-cta="mediaLink.delocalizedTracking"
          tabindex="-1"
          :target="mediaLink.targetAttribute"
          :to="mediaLink.url"
          @click="handlePromotionClick('img')"
        />
        <cms-base-gradient
          v-if="gradientStyle && gradientStyle !== 'off'"
          v-bind="{ gradientTone, gradientStyle, gradientGrid, gradientBreakpoints }"
          :alignment="gradientAlignment"
          :position="gradientPosition"
          :text-area-only-extent
        />
      </div>
      <div
        v-style:pb="{ sm: firstElement && usesPercentHeightSmMd && floatingNavigation ? '6.75rem' : '2.5rem', lg: 0 }"
        class="md:pointer-events-none"
        :class="[
          brandClasses.contentContainer,
          contentAlignmentSmall[overlaySmall],
          contentSpacingSmall[overlaySmall],
          contentAlignmentLarge[overlayLarge],
          { 'md:grid-area-stack': variant === 'standardHero' },
          { 'pt-30 lg:pt-24': firstElement && header.transparent },
        ]"
      >
        <div
          ref="textAreaRef"
          v-style:bg="backdrop"
          class="relative"
          :class="[
            brandClasses.content,
            { '<md:pt-8': hasCaption },
            contentSize[overlayWidth!] || 'w-full',
            overlaySmall?.includes('overlay') && backdrop ? 'p-2' : '<md:bg-transparent',
            blurClass,
          ]"
        >
          <heading-template />
          <p v-if="subtitle" v-style:c="subtitleColor" :class="subtitleStyle">
            <cms-plain-text :content field-name="subtitle" />
          </p>
          <cms-rich-text
            v-if="richText"
            v-style:c="richTextColor || subtitleStyle"
            class="pointer-within"
            :content="{ richText }"
          />
          <div
            v-if="linkType !== 'No-CTA' && targets.length"
            class="pointer-within i-flex"
            :class="[
              brandClasses.contentCtaContainer,
              { '<md:hidden': !overlaySmall?.includes('overlay') },
            ]"
          >
            <cms-shared-cta-group
              :button-class="[
                '',
                              ]"
              :class="ctaAlignmentLarge[overlayLarge]"
              :cta-type
              :equal-target-size
              :layout="brandClasses.contentCtaContainerInner"
              :multilink-style
              :multilink-title
              :size
              :targets
              :text-link-style="getTextLinkStyles(content)"
              @click="(i, target) => handleCtaClick(`cta${i + 1}`, target?.delocalizedTracking)"
            />
          </div>
          <cms-rich-text
            v-if="richTextBelowTargets"
            v-style:c="richTextBelowTargetsColor || subtitleStyle"
            class="pointer-within"
            :class="{ '<md:hidden': !overlaySmall?.includes('overlay') }"
            :content="{ richText: richTextBelowTargets }"
          />
        </div>
        <cms-base-caption
          v-if="hasCaption"
          :class="{ 'pt-12': firstElement && header.transparent }"
          v-bind="captionProps"
        />
      </div>
    </div>
    <cms-shared-cta-group
      v-if="linkType !== 'No-CTA' && targets.length && !overlaySmall?.includes('overlay')"
      :class="[
        equalTargetSize?.sm ? '' : ctaAlignmentSmall[overlaySmall],
        variant === 'standardHero' ? 'mt-6' : 'mt-2',
        { 'shrink-0': usesPercentHeight },
      ]"
      :cta-type
      :equal-target-size
      layout="f-col gap-4 px-4 md:!hidden"
      :multilink-style
      :multilink-title
      :size
      :targets
      :text-link-style="getTextLinkStyles(content)"
      @click="(i, target) => handleCtaClick(`cta${i + 1}`, target?.delocalizedTracking)"
    />
    <cms-rich-text
      v-if="richTextBelowTargets"
      v-style:c="richTextBelowTargetsColor || subtitleStyle"
      class="pointer-within mt-2 px-4 md:hidden"
      :class="[
        contentAlignmentSmall[overlaySmall],
        {
          '<md:hidden': overlaySmall?.includes('overlay'),
          'shrink-0': usesPercentHeight,
        },
      ]"
      :content="{ richText: richTextBelowTargets }"
    />
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { Responsive } from '#types/common'
import type { HeroContent, OverlayLarge, OverlaySmall } from '#types/components/cms/hero'
import { DynamicFieldsKey } from '#content/contexts/dynamic-fields'
import { SectionContextKey } from '#content/contexts/section'

const { controls = true, ...props } = defineProps<{
  content: HeroContent
  controls?: boolean
  paused?: boolean
  firstElement?: boolean
}>()

const {
  backdrop,
  backdropBlur,
  countdown,
  ctaType,
  equalTargetSize,
  eyebrowLogoResponsive,
  eyebrowLogoWidthResponsive,
  eyebrowText,
  eyebrowTextHtml,
  eyebrowTextColor,
  eyebrowTextStyle,
  gradientBreakpoints = { sm: true },
  gradientGrid,
  gradientStyle,
  gradientTone,
  isFullHeight,
  linkType,
  media,
  mediaTarget,
  multilinkStyle,
  multilinkTitle,
  name,
  overlayLarge,
  overlaySmall,
  overlayWidth,
  parallax,
  promotionTracking,
  richText,
  richTextColor,
  richTextBelowTargets,
  richTextBelowTargetsColor,
  subtitle,
  subtitleColor,
  subtitleStyle,
  targets = [],
  title,
  titleColor,
  titleStyle,
  variant = 'standardHero',
  mediaList,
  componentPercentHeight
} = props.content

countdown && provide(DynamicFieldsKey, { countdown })

const [DefineHeadingTemplate, HeadingTemplate] = createReusableTemplate()

const { $gtm } = useNuxtApp()
const appConfig = useAppConfig()
const cms = appConfig.components.cms
const { enableTextAreaOnlyGradient } = cms.gradient
const { size } = cms.sharedButton
const { brandClasses, brandStyles } = cms.hero
const { componentCorners } = cms.section
const { floatingNavigation, promoBarHeight } = appConfig.components.vf.header
const header = useHeaderStore()
const isTextAreaOnlyGradient = isTextAreaOnlyGradientEnabled(enableTextAreaOnlyGradient, gradientStyle)

const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)

const { fetchpriority, lazy } = inject(SectionContextKey, {} as SectionProvider)

const videoRef = ref<HTMLVideoElement>()
const gradientAreaRef = useTemplateRef('gradientAreaRef')
const textAreaRef = useTemplateRef('textAreaRef')
const mediaLink = mediaTarget || targets[0]
const responsiveMedia = (mediaList || [media]).map((m) => getResponsiveMedia(m))[0]
const aspectRatio = responsiveMedia?.aspectRatio || {}
const { height: gradientAreaHeight } = useElementSize(gradientAreaRef)
const { height: textAreaHeight } = useElementSize(textAreaRef)
const { top: gradientAreaTop } = useElementBounding(gradientAreaRef)
const { top: textAreaTop } = useElementBounding(textAreaRef)

const handleCtaClick = (suffix, delocalizedTracking) => {
  handlePromotionClick(suffix)
  if (delocalizedTracking)
    $gtm.push('hero.onClick', delocalizedTracking)
}

const contentPane = Object.keys(aspectRatio).reduce((acc, cur) => {
  acc[cur] = `calc(1 / ${aspectRatio[cur]} * 100%)`
  return acc
}, {})

const parallaxOffset = parallax?.config.innerOffset
const parallaxRef = useTemplateRef('parallaxRef')
const { config: parallaxConfig, translate: parallaxTranslate } = getOffsetParallaxConfig(parallaxOffset, {
  0: 0,
  1: 50,
  2: 70,
  3: 85,
  4: 100,
  5: 125,
})

if (parallaxConfig) {
  useParallaxController(parallaxRef, parallaxConfig)
}

const contentAlignmentLarge: Record<OverlayLarge, string> = {
  'left-left-top': 'md:items-start',
  'left-left-middle': 'md:items-center',
  'left-left-bottom': 'md:items-end',
  'left-right-middle': 'md:justify-end md:items-center',
  'center-left-middle': 'md:text-center md:items-center',
  'center-center-top': 'md:text-center md:justify-center md:items-start',
  'center-center-middle': 'md:text-center md:center',
  'center-center-bottom': 'md:text-center md:justify-center md:items-end',
  'center-right-middle': 'md:text-center md:justify-end md:items-center'
}

const contentAlignmentSmall: Record<OverlaySmall, string | any[]> = {
  'left-overlay': [{ '<md:grid-area-stack': variant === 'standardHero' }, '<md:pointer-events-none <md:items-center'],
  'left-bottom-overlay': [{ '<md:grid-area-stack': variant === 'standardHero' }, '<md:pointer-events-none <md:items-end'],
  'left-below': '<md:mt-6 <md:items-start',
  'center-overlay': [{ '<md:grid-area-stack': variant === 'standardHero' }, '<md:pointer-events-none <md:center <md:text-center'],
  'center-below': '<md:center <md:text-center'
}

const contentSpacingSmall: Record<OverlaySmall, string> = {
  'left-overlay': '',
  'left-bottom-overlay': '',
  'left-below': '<md:mt-6 <md:px-4',
  'center-overlay': '',
  'center-below': '<md:mt-6 <md:px-4'
}

const contentSize = {
  xs: 'w-10/12 md:w-1/3',
  sm: 'w-10/12 md:w-1/2',
  md: 'w-11/12 md:w-2/3',
  lg: 'w-full md:w-11/12'
}

const ctaAlignmentLarge: Partial<Record<OverlayLarge, string>> = {
  'center-left-middle': 'md:justify-center',
  'center-center-top': 'md:justify-center',
  'center-center-middle': 'md:justify-center',
  'center-center-bottom': 'md:justify-center',
  'center-right-middle': 'md:justify-center'
}

const ctaAlignmentSmall: Record<OverlaySmall, string> = {
  'left-overlay': '<md:items-start',
  'left-bottom-overlay': '<md:items-start',
  'left-below': '<md:items-start',
  'center-overlay': '<md:items-center',
  'center-below': '<md:items-center'
}

const gradientPositionSmall: Record<OverlaySmall, 'bottom' | 'center'> = {
  'left-overlay': 'center',
  'left-bottom-overlay': 'bottom',
  'left-below': 'bottom',
  'center-overlay': 'center',
  'center-below': 'bottom',
}

const gradientPositionLarge: Record<OverlayLarge, 'top' | 'bottom' | 'center'> = {
  'left-left-top': 'top',
  'left-left-middle': 'center',
  'left-left-bottom': 'bottom',
  'left-right-middle': 'center',
  'center-left-middle': 'center',
  'center-center-top': 'top',
  'center-center-middle': 'center',
  'center-center-bottom': 'bottom',
  'center-right-middle': 'center',
}

const gradientAlignmentSmall: Record<OverlaySmall, 'left' | 'center'> = {
  'left-overlay': 'left',
  'left-bottom-overlay': 'left',
  'left-below': 'left',
  'center-overlay': 'center',
  'center-below': 'center',
}

const gradientAlignmentLarge: Record<OverlayLarge, 'left' | 'center' | 'right'> = {
  'left-left-top': 'left',
  'left-left-middle': 'left',
  'left-left-bottom': 'left',
  'left-right-middle': 'right',
  'center-left-middle': 'left',
  'center-center-top': 'center',
  'center-center-middle': 'center',
  'center-center-bottom': 'center',
  'center-right-middle': 'right',
}

const gradientPosition: Responsive<'top' | 'bottom' | 'center'> = {
  sm: gradientPositionSmall[overlaySmall],
  md: gradientPositionLarge[overlayLarge],
}

const gradientAlignment: Responsive<'left' | 'center' | 'right'> = {
  sm: gradientAlignmentSmall[overlaySmall],
  md: gradientAlignmentLarge[overlayLarge],
}

const getTextAreaOnlyExtent = (
  pos?: 'top' | 'bottom' | 'center',
) => {
  const fallbackExtent = pos === 'center' ? '67%' : '33%'
  if (!textAreaHeight.value)
    return fallbackExtent

  const extent = pos === 'bottom'
    ? gradientAreaHeight.value && textAreaTop.value && gradientAreaTop.value
      ? gradientAreaHeight.value - (textAreaTop.value - gradientAreaTop.value)
      : textAreaHeight.value
    : pos === 'center'
      ? gradientAreaHeight.value
        ? (gradientAreaHeight.value + textAreaHeight.value) / 2
        : undefined
      : textAreaHeight.value

  if (!extent || extent <= 0)
    return fallbackExtent

  return `${Math.ceil(extent)}px`
}

const textAreaOnlyExtent = computed<Responsive<string> | undefined>(() => {
  if (!isTextAreaOnlyGradient)
    return undefined

  const smallExtent = getTextAreaOnlyExtent(gradientPosition.sm)
  const mediumExtent = getTextAreaOnlyExtent(gradientPosition.md)
  const resolvedExtent = smallExtent || mediumExtent

  if (!resolvedExtent)
    return undefined

  return {
    sm: resolvedExtent,
    ...(mediumExtent && { md: mediumExtent }),
    lg: mediumExtent || resolvedExtent,
  }
})

const headerHeight = computed(() => (header.transparent ? header.height.promoBar : header.height.full) || 0)
const percentHeightHeaderOffsetPx = useHydrationSafeHeaderOffsetPx()

const hasComponentPercentHeight = componentPercentHeight !== undefined
const usesPercentHeightSmMd = hasPercentHeightAtBreakpoint(componentPercentHeight?.small)
const usesPercentHeightLg = hasPercentHeightAtBreakpoint(componentPercentHeight?.large)
const usesPercentHeight = usesPercentHeightSmMd || usesPercentHeightLg
const anchorSmall = componentPercentHeight?.anchorSmall
const anchorLarge = componentPercentHeight?.anchorLarge

const percentHeightStyle = computed(() =>
  getPercentHeightCssVars(componentPercentHeight, percentHeightHeaderOffsetPx.value),
)

const parallaxPaddingBottom = Object.fromEntries(
  Object.entries(contentPane).filter(([bp]) =>
    bp === 'lg' ? !usesPercentHeightLg : !usesPercentHeightSmMd,
  ),
)

const objectPosition = getResponsiveObjectPosition(anchorSmall, anchorLarge)
const imageProps = { class: 'full' }

const textAlignmentLarge = overlayLarge?.split('-')[0] || 'left'
const textAlignmentSmall = overlaySmall?.split('-')[0] || 'center'

const { hasCaption, captionProps } = getCaptionConfig(props.content)

const roundedCornersClasses = getRoundedCorners(props.content, componentCorners)

const blurClass = backdropBlur && (backdropBlur === 'low' ? 'backdrop-blur-xs' : 'backdrop-blur-md')

useAutoplay(videoRef)

watch(() => props.paused, (paused) => paused ? videoRef.value?.pause() : videoRef.value?.play())
</script>
